import { useCallback, useRef, useState } from 'react';
import { Box, Paper, Typography } from '@mui/material';

import styles from './drop-and-drap.module.css';
interface DrapAndDropProps {
  handleDropUpload: (files: File[]) => void;
  className: string;
  isHaveFileFailed: boolean;
  allowedExtensions: string[];
}

const DrapAndDrop = ({
  handleDropUpload,
  className,
  isHaveFileFailed,
  allowedExtensions,
}: DrapAndDropProps) => {
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Helper function to determine paper class
  const getPaperClassName = () => {
    if (isHaveFileFailed) return styles.paperIconError;
    if (dragActive) return styles.paperActive;
    return styles.paperDefault;
  };

  // Helper function to determine upload icon class
  const getUploadIconClassName = () => {
    if (isHaveFileFailed) return styles.uploadIconError;
    if (dragActive) return styles.uploadIconActive;
    return styles.uploadIconDefault;
  };

  const handleDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget.contains(e.relatedTarget as Node)) {
      return;
    }
    setDragActive(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);
      handleDropUpload(Array.from(e.dataTransfer.files ?? []));
    },
    [handleDropUpload]
  );

  const handleSelectFile = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (files) {
        handleDropUpload(Array.from(files));
      }
      event.target.value = '';
    },
    [handleDropUpload]
  );

  const handleIconClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  return (
    <Box
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      className={className}
      data-testid='drop-zone'
      sx={{ position: 'relative' }}
    >
      <Paper
        elevation={0}
        onClick={handleIconClick}
        className={`${styles.paper} ${getPaperClassName()}`}
      >
        <input
          type='file'
          hidden
          onChange={handleSelectFile}
          ref={fileInputRef}
          multiple
          accept={allowedExtensions.map((ext) => `.${ext}`).join(',')}
        />

        <Box className={`${styles.uploadIcon} ${getUploadIconClassName()}`} />
        <Box sx={{ display: 'inline-block', alignItems: 'center', textAlign: 'center' }}>
          <Typography component='span' sx={{ color: '#49454F', fontSize: '16px' }}>
            ファイルをドラッグまたは
          </Typography>
          <Typography component='span' sx={{ color: '#1e5cc2', fontSize: '16px' }}>
            クリックしてアップロード
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default DrapAndDrop;
